import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { CartItem } from '../components/restaurant/CartItem';
import { CheckoutUpsellModal } from '../components/restaurant/CheckoutUpsellModal';
import { useCart } from '../lib/cart';
import { formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { useKitchenPause } from '../lib/kitchen-pause-context';
import { useLanguage } from '../lib/i18n';
import { useTableOrder } from '../lib/table-context';
import { useRestaurantScheduleStatus } from '../lib/schedule';
import { ShoppingBag, ArrowLeft, Store } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  const {
    cart,
    updateQuantity,
    removeItem,
    getTotal,
    clearCart,
    getDiscount
  } = useCart();
  const {
    isKitchenPaused,
    monitorRestaurant
  } = useKitchenPause();
  const {
    t
  } = useLanguage();
  const {
    tableOrder,
    isTableOrder
  } = useTableOrder();
  // Schedule-based open/closed enforcement
  const scheduleStatus = useRestaurantScheduleStatus(cart.restaurantSlug);
  const isClosedBySchedule = scheduleStatus.isClosedForOrdering;
  const isNatucafeQrTable = isTableOrder && !!tableOrder && String(tableOrder.restaurantId) === '19';
  // ─── Crack Pollo (id 20) upsell modal ────────────────────────────────
  const isCrackPolloCart = String(cart.restaurantId) === '20';
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellAlreadyShown, setUpsellAlreadyShown] = useState(false);
  const handleCheckoutClick = () => {
    if (isClosedBySchedule) {
      alert(`${cart.restaurantName || 'El restaurante'} está cerrado en este momento. ${scheduleStatus.nextOpeningHint || 'Vuelve durante nuestro horario de atención.'}`);
      return;
    }
    if (isCrackPolloCart && cart.items.length > 0 && !upsellAlreadyShown) {
      setShowUpsellModal(true);
      return;
    }
    navigate('/checkout');
  };
  const handleUpsellContinue = () => {
    setShowUpsellModal(false);
    setUpsellAlreadyShown(true);
    navigate('/checkout');
  };
  // Register restaurant with central kitchen pause monitor
  useEffect(() => {
    if (cart.restaurantId) {
      console.log('[NOIZA][CartPage] Registering restaurant:', cart.restaurantId);
      monitorRestaurant(cart.restaurantId);
    }
  }, [cart.restaurantId, monitorRestaurant]);
  const subtotal = getTotal();
  const discount = getDiscount();
  // In a real app, delivery fee would be calculated based on distance/settings
  const deliveryFeeEstimate = cart.items.length > 0 ? 1500 : 0;
  const total = subtotal + deliveryFeeEstimate;
  if (cart.items.length === 0) {
    return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5192">
        <MarketplaceHeader data-id="element-5193" />
        <main className="flex-1 flex items-center justify-center py-12" data-id="element-5194">
          <EmptyState icon={ShoppingBag} title={t('cart_empty')} description={t('cart_empty_desc')} actionLabel={t('explore')} onAction={() => navigate('/restaurants')} data-id="element-5195" />
        </main>
        <Footer data-id="element-5196" />
      </div>;
  }
  return <div className="min-h-screen w-full flex flex-col bg-brand-offwhite" data-id="element-5197">
      <MarketplaceHeader data-id="element-5198" />

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-8" data-id="element-5199">
        <div className="flex items-center justify-between mb-8" data-id="element-5200">
          <div className="flex items-center gap-4" data-id="element-5201">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-400 hover:text-gray-900 rounded-full hover:bg-gray-50 transition-colors" data-id="element-5202">
              <ArrowLeft className="w-6 h-6" data-id="element-5203" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-5204">
              {t('cart_title')}
            </h1>
          </div>
          <button onClick={clearCart} className="text-sm font-medium text-red-500 hover:text-red-600 transition-colors" data-id="element-5205">
            {t('cart_clear')}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-8" data-id="element-5206">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-100 flex items-center gap-3" data-id="element-5207">
            <Store className="w-5 h-5 text-gray-500" data-id="element-5208" />
            <span className="font-semibold text-gray-900" data-id="element-5209">
              {cart.restaurantName}
            </span>
          </div>

          <div className="px-6 py-2" data-id="element-5210">
            {cart.items.map(item => <CartItem key={item.id} item={item} onUpdateQuantity={updateQuantity} onRemove={removeItem} data-id="element-5211" />)}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-8" data-id="element-5212">
          <div className="space-y-4" data-id="element-5213">
            <div className="flex justify-between text-gray-600" data-id="element-5214">
              <span data-id="element-5215">{t('subtotal')}</span>
              <span data-id="element-5216">{formatCurrency(subtotal + (discount?.amount ?? 0))}</span>
            </div>
            {discount && <div className="flex justify-between text-emerald-600 font-medium" data-id="element-5217">
                <span data-id="element-5218">{discount.label}</span>
                <span data-id="element-5219">-{formatCurrency(discount.amount)}</span>
              </div>}
            <div className="flex justify-between text-gray-600" data-id="element-5220">
              <span data-id="element-5221">{t('delivery_fee')}</span>
              <span data-id="element-5222">{formatCurrency(deliveryFeeEstimate)}</span>
            </div>
            <div className="pt-4 border-t border-gray-100 flex justify-between text-lg font-bold text-gray-900" data-id="element-5223">
              <span data-id="element-5224">{t('total')}</span>
              <span data-id="element-5225">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <Button onClick={handleCheckoutClick} className="w-full h-14 text-lg" disabled={isKitchenPaused || isClosedBySchedule} data-id="element-5226">
          {isClosedBySchedule ? `🔒 Cerrado${scheduleStatus.nextOpeningHint ? ` — ${scheduleStatus.nextOpeningHint}` : ''}` : isKitchenPaused ? t('kitchen_paused') : isNatucafeQrTable ? 'Terminar Pedido' : t('cart_checkout')}
        </Button>
      </main>

      {isCrackPolloCart && cart.restaurantId && cart.restaurantName && <CheckoutUpsellModal isOpen={showUpsellModal} onClose={() => setShowUpsellModal(false)} onContinue={handleUpsellContinue} restaurantId={cart.restaurantId} restaurantName={cart.restaurantName} restaurantSlug={cart.restaurantSlug ?? undefined} data-id="element-5227" />}

      <Footer data-id="element-5228" />
    </div>;
}